import React from 'react';
import {
  Box,
  Grid,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import {
  WiDaySunny,
  WiCloudy,
  WiRain,
  WiSnow,
  WiThunderstorm,
  WiFog,
} from 'react-icons/wi';

interface WeatherStat {
  weather_condition: string;
  occurrence_count: number;
  mood_distribution?: {
    [mood: string]: number;
  };
}

const weatherIcons: { [key: string]: any } = {
  'Clear': WiDaySunny,
  'Clouds': WiCloudy,
  'Rain': WiRain,
  'Snow': WiSnow,
  'Thunderstorm': WiThunderstorm,
  'Fog': WiFog,
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const LocationStats: React.FC = () => {
  const { locationStats } = useAnalytics();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tooltipBg = useColorModeValue('#FFFFFF', '#1A202C');
  const textColor = useColorModeValue('gray.800', 'gray.200');

  const weatherData = locationStats.weatherStats.map((stat, index) => ({
    name: stat.weather_condition,
    value: stat.occurrence_count,
    color: COLORS[index % COLORS.length],
  }));

  const getMoodColor = (label: string) => {
    switch (label) {
      case 'Amazing': return 'yellow.400';
      case 'Happy': return 'green.400';
      case 'Good': return 'blue.400';
      case 'Okay': return 'purple.400';
      case 'Down': return 'red.400';
      default: return 'blue.400';
    }
  };

  const renderMoodDistribution = (stat: WeatherStat) => {
    if (!stat.mood_distribution) return null;

    return Object.entries(stat.mood_distribution).map(([mood, count]) => {
      const percentage = (count / stat.occurrence_count) * 100;
      return (
        <Tooltip key={mood} label={`${mood}: ${count} entries (${percentage.toFixed(1)}%)`}>
          <Box
            h="20px"
            flexGrow={count}
            bg={getMoodColor(mood)}
            borderRadius="sm"
          />
        </Tooltip>
      );
    });
  };

  return (
    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
      {/* Weather Distribution Chart */}
      <Box
        p={6}
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        height="400px"
      >
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Weather Distribution
        </Text>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={weatherData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={120}
              label
            >
              {weatherData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={{
                backgroundColor: tooltipBg,
                border: '1px solid #CBD5E0',
              }}
              formatter={(value: any, name: any) => [value, name]}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Weather Mood Distribution */}
      <Box
        p={6}
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
      >
        <Text fontSize="lg" fontWeight="bold" mb={6}>
          Weather & Mood Distribution
        </Text>
        <VStack spacing={4} align="stretch">
          {locationStats.weatherStats.map((stat) => {
            const WeatherIcon = weatherIcons[stat.weather_condition] || WiCloudy;
            return (
              <Box
                key={stat.weather_condition}
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
              >
                <HStack justify="space-between" mb={2}>
                  <HStack>
                    <Icon as={WeatherIcon} boxSize={6} color={textColor} />
                    <Text fontWeight="medium">{stat.weather_condition}</Text>
                  </HStack>
                  <Text fontSize="sm" color={textColor}>
                    {stat.occurrence_count} entries
                  </Text>
                </HStack>
                <HStack spacing={2} mt={2}>
                  {renderMoodDistribution(stat)}
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Grid>
  );
};

export default LocationStats; 